import { useState } from "react";
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
  Grid,
  IconButton,
  Collapse,
} from "@mui/material";
import Iconify from "../../components/Iconify";
import SvgIconStyle from '../../components/SvgIconStyle';
// hooks
import useResponsive from "../../hooks/useResponsive";
import { styled } from "@mui/material/styles";
import { fYYYYMMDD, fYYYYMMDDKST, fKrDateAbsoulte, fKrDateAbsoulteMD } from "../../utils/formatTime";
import { LoadingButton } from "@mui/lab";


const WeekStr = styled(Typography)(({ theme }) => ({
  width: 42,
  height: 42,
  padding: 0,
  margin: 0,
  alignItems: "center",
  justifyContent: "center",
  display: "flex",
  color: theme.palette.text.disabled,
  ...theme.typography.caption,
  [theme.breakpoints.down('md')]: {
    width: 36,
    height: 36,
  },
}));


const WeekBtn = styled(Button)(({theme}) => ({
  ...theme.typography.body1,
  minWidth: 36,
  minHeight: 36,
  width: 42,
  height: 42,
  padding: 0,
  margin: 0,
  fontWeight: 400,
  borderRadius: 21,
  boxShadow: "none",
  [theme.breakpoints.down('md')]: {
    width: 36,
    height: 36,
    ...theme.typography.body2,
  },
}));

const TimeButton = styled(Button)(({theme}) => ({
  ...theme.typography.body1,
  maxWidth: 106,
  maxHeight: 56,
  width: 106,
  height: 56,
  padding: 0,
  margin: 0,
  color: theme.palette.text.secondary,
  boxShadow: theme.customShadows.card,
  [theme.breakpoints.down('md')]: {
    ...theme.typography.caption,
    width: 89,
    height: 48,
  },
}));


const WEEK_TITLE = ["일", "월", "화", "수", "목", "금", "토"];
const HOUR_TITLE = [
  { label: "오전 10시", value: "10:00", start: "10:00", end: "11:00" },
  { label: "오전 11시", value: "11:00", start: "11:00", end: "12:00" },
  { label: "정오 12시", value: "12:00", start: "12:00", end: "13:00" },
  { label: "오후 1시", value: "13:00", start: "13:00", end: "14:00" },
  { label: "오후 2시", value: "14:00", start: "14:00", end: "15:00" },
  { label: "오후 3시", value: "15:00", start: "15:00", end: "16:00" },
  { label: "오후 4시", value: "16:00", start: "16:00", end: "17:00" },
  { label: "오후 5시", value: "17:00", start: "17:00", end: "18:00" },
  { label: "오후 6시", value: "18:00", start: "18:00", end: "19:00" },
  { label: "오후 7시", value: "19:00", start: "19:00", end: "20:00" },
  { label: "오후 8시", value: "20:00", start: "20:00", end: "21:00" },
];

const getDates = (start, range, includeToday=false) => {
  const today = new Date();
  const todayStr = fYYYYMMDDKST(today);
  const dates = {
    start: fYYYYMMDD(start),
    end: fYYYYMMDD(new Date(start.getTime() + (range - 1) * 24 * 60 * 60 * 1000)),
    days: [],
  };
  for (let i = 0; i < range; i ++) {
    const date = new Date(start.getTime() + i * 24 * 60 * 60 * 1000);
    const dateStr = fYYYYMMDD(date);
    const oneDay = {
      dateStr: dateStr,
      day: date.getDay(),
      available: includeToday ? (dateStr >= todayStr) : (dateStr > todayStr),
      hours: [...HOUR_TITLE],
    };
    dates.days.push(oneDay);
  }
  return dates;
}

const get14DatesFromSunday = (start, includeToday) => {
  var day = start.getDay();
  var diff = start.getDate() - day;
  return getDates(new Date(start.setDate(diff)), 14, includeToday);
}

export default function Check3WayAvailableTime ({ send, isSubmitting, isModify=false, reselect=false, timeComment=null, previousTimes=null, meetingCount=0 }) {
  const includeToday = meetingCount > 0;
  const DATES = get14DatesFromSunday(new Date(), includeToday);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedDateTime, setSelectedDateTime] = useState({});
  const [isExpanded, setIsExpanded] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const mdUp = useResponsive('md', 'up');

  const toggleDate = (dateStr) => {
    if (selectedDate === dateStr) {
      setSelectedDate('');
    } else {
      setSelectedDate(dateStr);
    }
  };

  const selectTime = (item) => {
    const newDict = {...selectedDateTime};
    if (newDict[selectedDate]) {
      if (newDict[selectedDate].find((d) => d.value === item.value)) {
        newDict[selectedDate] = newDict[selectedDate].filter((d) => d.value !== item.value);
      } else {
        newDict[selectedDate].push(item)
      }
    } else {
      newDict[selectedDate] = [item]
    }

    if (newDict[selectedDate].length === 0) {
      delete newDict[selectedDate];
    } else {
      newDict[selectedDate].sort((a, b) => a.value > b.value ? 1 : -1);
    }
    setSelectedDateTime(newDict);
  };

  const sendTime = () => {
    if (Object.keys(selectedDateTime).length < 3) {
      enqueueSnackbar('날짜를 3개 이상 선택해주세요', { variant: 'error' });
    } else {
      const timeRange = Object.keys(selectedDateTime).map((d) => {
        return {
          date: d,
          intervals: selectedDateTime[d],
        }
      });
      send(timeRange);
    }
  }

  return (
    <Card>
      <CardHeader
        title={`${reselect ? "" : "사전 "}미팅이 가능한 날짜와 시간을 ${reselect ? "다시 " : "" }선택해주세요.`}
        subheader={reselect ? "" : "회사와 겹치는 시간대가 생기도록 최대한 많은 시간대를 선택해주세요. (한국시간 기준)"}
        sx={{ px: {xs: 3, md: 6}, pt: { xs: 3, md: 6} }}
      />
      <CardContent sx={{ pt: 2, px: {xs : 3, md: 6}, mb: 3 }}>
        {(reselect || isModify) && (
          <Stack spacing={2} sx={{ pb: 3 }}>
            {reselect && timeComment && (
            <Box sx={{ px: 2, py: 1.5, backgroundColor: 'rgba(255, 72, 66, 0.08);', border: '1px solid', borderColor: (theme) => theme.palette.error.light, borderRadius: 1 }}>
              <Stack direction="row" spacing={1.5}>
                <SvgIconStyle src={`/icons/icon-chat-bubble-dots-mono.svg`} sx={{ color: "error.main" }} />
                <Stack direction={{ xs: "column", md: "row" }} alignItems={mdUp ? "center" : "flex-start"} justifyContent="space-between" sx={{ width: 1 }} spacing={2}>
                  <Stack spacing={0.5}>
                    <Typography variant="body1" color="error.darker" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {timeComment}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Box>
            )}
            <Box sx={{ p:2, backgroundColor: 'grey.200', borderRadius: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center"
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{
                  cursor: 'pointer',
                }}
              >
                <Stack direction="row" spacing={1} sx={{ flexGrow: 1 }}>
                  <Iconify icon="eva:refresh-outline" color="text.secondary" sx={{ width: 24, height: 24 }} />
                  <Typography variant="subtitle2">
                    이전에 선택했던 미팅 가능 시간
                  </Typography>
                </Stack>
                <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)}>
                  <Iconify icon={`eva:chevron-${isExpanded ? 'up' : 'down'}-outline`} color="text.secondary" sx={{ width: 24, height: 24 }} />
                </IconButton>
              </Stack>
              {previousTimes && (
              <Collapse
                sx={{ pl: 4 }}
                in={isExpanded}
                {...(isExpanded ? { timeout: 500 } : {})}
              >
                {typeof previousTimes === 'object' && Array.isArray(previousTimes) && previousTimes?.sort((a, b) => a.date > b.date ? 1 : -1)?.map((pt, i) => (
                  <Stack key={i}>
                    <Typography>
                      <b>{WEEK_TITLE[new Date(pt.date).getUTCDay()]}요일({Number.parseInt(pt.date.slice(8))}일)</b> {pt.intervals?.map((item) => `${Number.parseInt(item.start.slice(0, 2))}시`).join(', ')}
                    </Typography>
                  </Stack>
                ))}
                {typeof previousTimes === 'object' && !Array.isArray(previousTimes) && Object.keys(previousTimes).sort((a, b) => WEEK_TITLE.indexOf(a) > WEEK_TITLE.indexOf(b) ? 1 : -1).map((date, index) => {
                  if (previousTimes[date].length === 0) {
                    return null;
                  }
                  return (
                    <Stack key={index} direction="row" spacing={1}>
                      <Typography sx={{ minWidth: "55px" }}>
                        <b>매주 {date}요일</b>
                      </Typography>
                      <Typography sx={{ wordBreak: "keep-all" }}>
                        {previousTimes[date].map((item) => `${Number.parseInt(item.start.slice(0, 2))}시`).join(', ')}
                      </Typography>
                    </Stack>
                  )
                })}
              </Collapse>
              )}
            </Box>
          </Stack>
        )}
        <Stack
          direction={{xs: "column", md: "row"}}
          spacing={{ xs: 3, md: 6.25 }}
          display="flex"
          justifyContent="center"
          alignItems={{ xs: "center", md: "start" }}
          sx={{
            mt: 5,
            mb: { xs: 3, md: 13.7 },
            ...selectedDate && {
              mb: { xs: 4, md: 8 },
            }
          }}
        >
          <Stack spacing={2} sx={{ maxWidth: '350px', width: 1 }}>
            <Typography variant="subtitle1" sx={{ mx: { xs: 1.5, md: 2 } }}>
              {selectedDate ? fKrDateAbsoulte(selectedDate) : `${fKrDateAbsoulte(DATES.start)} ~ ${fKrDateAbsoulteMD(DATES.end)}`}
            </Typography>

            {/* calendar */}
            <Stack sx={{ width: 1 }}>
              <Grid container columnSpacing={0.5} columns={12} rowSpacing={1}>
                {WEEK_TITLE.map((item, index) => (
                  <Grid item xs={1.7} key={index}>
                    <WeekStr key={index}>
                      {item}
                    </WeekStr>
                  </Grid>
                ))}
                {DATES.days.map((item, index) => (
                  <Grid item xs={1.7} key={index}>
                    <WeekBtn
                      key={index}
                      disabled={!item.available}
                      sx={{
                        ...(selectedDate === item.dateStr) && {
                          color: (theme) => theme.palette.common.white,
                          backgroundColor: "info.main",
                          '&:hover': {
                            backgroundColor: "info.dark",
                          },
                        }
                      }}
                      variant={(selectedDate === item.dateStr) ? "contained" : "text"}
                      onClick={() => toggleDate(item.dateStr)}
                    >
                      {Number.parseInt(item.dateStr.slice(8))}
                    </WeekBtn>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Stack>

          {/* time buttons */}
          <Grid container columns={12}
            sx={{
              maxWidth: { xs: '350px', md: '420px' },
              width: 1,
              '& .MuiGrid-item': {
                px: { xs: 0.625, md: 1},
                py: { xs: 0.625, md: 1.25},
              },
            }}
          >
            {selectedDate && DATES.days.find((item) => item.dateStr === selectedDate).hours.map((item, index) => (
              <Grid item container xs={3} key={index}>
                <TimeButton
                  onClick={() => selectTime(item)}
                  sx={{
                    ...(selectedDateTime?.[selectedDate]?.find((d) => d.value === item.value)) && {
                      color: (theme) => theme.palette.common.white,
                      backgroundColor: "info.main",
                      fontWeight: 700,
                      '&:hover': {
                        backgroundColor: "info.dark",
                      },
                    }
                  }}
                  variant={(item.selected) ? "contained" : "text"}
                >
                  {item.label}
                </TimeButton>
              </Grid>
            ))}
          </Grid>
        </Stack>

        {/* description */}
        {Object.keys(selectedDateTime).length > 0 && (
        <>
          <Typography variant="h6" paragraph>
            선택한 시간
          </Typography>
          <Stack
            sx={{
              backgroundColor: (theme) => theme.palette.grey[100],
              borderRadius: 1.5,
              px: 2.5,
              py: 3,
              mb: { xs: 2, md: 12 },
            }}
          >
            {Object.keys(selectedDateTime).sort().map((date, index) => {
              if (selectedDateTime[date].length === 0) {
                return null;
              }
              return (
                <Typography key={index}>
                  <b>{WEEK_TITLE[DATES.days.find((v) => v.dateStr === date).day]}요일({Number.parseInt(date.slice(8))}일)</b> {selectedDateTime[date].map((item) => `${Number.parseInt(item.start.slice(0, 2))}시`).join(', ')}
                </Typography>
              )
            })}
          </Stack>
        </>
        )}
        <LoadingButton
          size="large"
          variant="contained"
          fullWidth
          onClick={() => sendTime()}
          disabled={Object.keys(selectedDateTime).length === 0}
          loading={isSubmitting}
        >
          완료
        </LoadingButton>
      </CardContent>
    </Card>
  )
}